/**
 * Graph Visualizer
 *
 * Generates visual representations of the architecture graph:
 * - DOT format (for Graphviz)
 * - Interactive HTML (using viz.js)
 *
 * All behavior lives on the injectable GraphVisualizer class so webpieces DI +
 * @DocumentDesign can wire it — module-scope functions are a dead end the DI
 * graph can't reach.
 */

import * as fs from 'fs';
import * as path from 'path';
import { execSync } from 'child_process';
import type { EnhancedGraph } from './graph-sorter';
import type { ApiRef, ApiRelation, ApiRelationKind } from './api-usage/api-relations';
import { GraphNames, NodeIdOwner } from './graph-names';
import { LevelBand, LevelBandLayout } from './graph-level-bands';
import { ProjectAdjacency, ProjectCycleDetector } from './graph-cycles';
import { ResponsibilitiesRenderer } from './graph-responsibilities';
import { GraphNodeMenu } from './graph-node-menu';
import { toError } from '../toError';

/**
 * Framework (libType) colors for visualization — nodes are filled by the FIRST
 * env in their set that has a color, so it is obvious at a glance which side a
 * project targets. A project's full env set is shown in the label.
 */
const FRAMEWORK_COLORS: Record<string, string> = {
    angular: '#FCE4EC', // pink   - Angular front-end
    react: '#E3F2FD', // blue   - React front-end
    browser: '#EDE7F6', // purple - browser (front-end base env)
    express: '#E8F5E9', // green  - Express / server side
    node: '#FFF9C4', // yellow - node (server base env)
};

const DEFAULT_FRAMEWORK_COLOR = '#F5F5F5'; // grey - unknown/empty env set

/**
 * Directory (repo-relative) that the committed architecture HTML lives in.
 * Node click-through links are computed relative to this so they resolve when
 * the file is opened straight from the checkout.
 */
const ARCH_OUTPUT_DIR = 'architecture';

/** Contracts named on one `implements` edge label before it truncates to "+N more". */
const MAX_EDGE_LABEL_APIS = 4;


/**
 * One node's design page, handed to the browser so the node menu can offer "View Design" for the
 * boxes that HAVE one and omit the item entirely for the boxes that do not.
 *
 * The link is data on the PAGE now, not a `URL=` attribute in the DOT: a URL made viz.js wrap the
 * box in an `<a>` and clicking navigated straight out, which the menu replaces.
 */
export class DesignLink {
    constructor(
        public readonly nodeId: string,
        public readonly href: string
    ) {}
}

export class VisualizationPaths {
    htmlPath: string;

    constructor(htmlPath: string) {
        this.htmlPath = htmlPath;
    }
}

/**
 * The token the client scripts carry where the DOT belongs. It must appear EXACTLY ONCE in a compiled
 * client — the inliner is a blind split/join, so a second occurrence (in a comment, say) would be
 * replaced by the entire DOT too, bloating every generated page.
 */
export const CLIENT_DOT_PLACEHOLDER = '__' + 'DOT' + '__';

/** Same contract as CLIENT_DOT_PLACEHOLDER, for the node → design.html links the menu offers. */
export const CLIENT_DESIGN_LINKS_PLACEHOLDER = '__' + 'DESIGN_LINKS' + '__';

/**
 * Read a compiled browser client sitting beside this file.
 *
 * It is generated from the matching `.client.ts` by tsc, so it exists in `dist` and in the published
 * tarball but NOT in a source checkout. The error says that outright rather than surfacing a bare
 * ENOENT, because "the build has not run" and "the file is missing" look identical otherwise.
 */
// webpieces-disable no-function-outside-class -- module-level resolver for the default injected into GraphVisualizer/RuntimeHtmlPage; making it a class would need a container in a module both of them load eagerly
export function readCompiledClient(name: string): string {
    const file = path.join(__dirname, name);
    if (!fs.existsSync(file)) {
        throw new Error(
            `${name} not found beside ${__dirname}. It is COMPILED from ${name.replace(/\.js$/, '.ts')} `
            + 'by tsc, so it only exists after a build — run the package build, or inject the text.');
    }
    return fs.readFileSync(file, 'utf-8');
}

export class GraphVisualizer {
    private readonly names = new GraphNames();
    private readonly responsibilities = new ResponsibilitiesRenderer();
    private readonly bandLayout = new LevelBandLayout();
    private readonly cycles = new ProjectCycleDetector();
    /** The ONE floating-node-menu implementation, shared with the per-project design pages. */
    private readonly nodeMenu = new GraphNodeMenu();

    /**
     * How to obtain the browser client's text. Injected so HTML generation does not depend on BUILD
     * ORDER: the default reads the compiled sibling, which exists in the tarball and in dist but NOT in
     * a source checkout (the source there is .client.ts). A unit test running from source supplies the
     * text itself rather than requiring the package to have been built first.
     */
    constructor(
        private readonly clientJs: () => string =
        (): string => readCompiledClient('graph-visualizer.client.js'),
    ) {}

    /**
     * A project tagged `drawOnGraph:false` is hidden from the rendered graph —
     * its node, its rank placement, its dropdown option, its responsibilities
     * card, and every edge touching it are all omitted. It stays in the JSON.
     */
    private isHidden(entry: EnhancedGraph[string]): boolean {
        return entry.drawOnGraph === false;
    }

    /**
     * Fill color for an env set — the color of the first env in the set that has
     * a known color, else the default.
     */
    private frameworkColor(frameworks: string[]): string {
        for (const env of frameworks) {
            const color = FRAMEWORK_COLORS[env];
            if (color !== undefined) return color;
        }
        return DEFAULT_FRAMEWORK_COLOR;
    }

    /**
     * Role border styling — fill stays keyed on framework; the border shows a
     * project's ROLE at a glance. Server and client are the top-level runnable
     * nodes, so they get bold, colored borders to stand out:
     *   server       → thick GREEN border  (a runnable server app)
     *   app          → thick BLUE border   (a runnable non-HTTP app, e.g. a tooling app)
     *   bundle       → thick PURPLE border (an aggregator that bundles several apps)
     *   client       → thick RED border    (a client app, e.g. angular)
     *   designed-lib → bold border         (a library with a generated @DocumentDesign design)
     *   lib / other  → plain thin border
     */
    private roleBorderAttrs(role: string): string {
        if (role === 'server') return ', color="green", penwidth=3';
        if (role === 'app') return ', color="#1976d2", penwidth=3';
        if (role === 'bundle') return ', color="#6A1B9A", penwidth=3';
        if (role === 'client') return ', color="red", penwidth=3';
        if (role === 'api-lib') return ', color="#EF6C00", penwidth=2';
        if (role === 'designed-lib') return ', penwidth=2';
        return '';
    }

    /**
     * Edge styling by API-relation kind (why the edge exists):
     *   implements       → BLACK dashed (a controller serves this api-lib's contract)
     *   uses             → BLACK solid (a generated client calls it) — same as a
     *                      plain library import, since a plain dependency IS a use.
     *   uses-implements  → BLUE dashed, thicker (does both — implements some
     *                      contracts of the api-lib, uses others)
     *   plain lib (none) → the default thin black solid arrow, unchanged.
     * `kind` is undefined for every non-api-lib dependency edge.
     */
    private edgeAttrs(kind: ApiRelationKind | undefined): string {
        if (kind === 'implements') return 'style=dashed';
        if (kind === 'uses-implements') return 'style=dashed, color="#1976d2", penwidth=2';
        return '';
    }

    /**
     * The DOT attribute list for ONE dependency edge. An edge that IMPLEMENTS contracts is also
     * LABELED with them: the dashed line alone says "something here is implemented", which reads as
     * "the tool did not detect anything" to everyone who has not memorized the legend. Naming the
     * contracts makes `auth-store-api` visibly resolve to the server that serves it — the single
     * most important relationship in a microservice architecture, and the one the diagram was
     * silent about.
     */
    private edgeDot(from: string, to: string, relation: ApiRelation | undefined): string {
        const attrs: string[] = [];
        const styling = this.edgeAttrs(relation?.kind);
        if (styling !== '') attrs.push(styling);
        const served = (relation?.implements ?? []).map((ref: ApiRef) => ref.api);
        if (served.length > 0) attrs.push(`label="implements: ${this.labelledApis(served)}", fontsize=9`);
        const suffix = attrs.length === 0 ? '' : ` [${attrs.join(', ')}]`;
        return `  "${from}" -> "${to}"${suffix};\n`;
    }

    /**
     * The contract names for an edge label, sorted and capped so a shared api-lib serving a dozen
     * contracts cannot blow the edge label up into a wall of text. The truncation is stated in the
     * label ("+N more") rather than silent — the full list is in dependencies.json.
     */
    private labelledApis(apis: string[]): string {
        const sorted = [...apis].sort();
        if (sorted.length <= MAX_EDGE_LABEL_APIS) return sorted.join(', ');
        const shown = sorted.slice(0, MAX_EDGE_LABEL_APIS).join(', ');
        return `${shown} +${sorted.length - MAX_EDGE_LABEL_APIS} more`;
    }

    /**
     * Href for a node's design page: the project's committed design.html, made
     * relative to architecture/dependencies.html. Returns null when the project
     * has no generated DI design (no design.json → no design page exists).
     */
    private designHtmlHref(designFile: string | undefined): string | null {
        if (!designFile) return null;
        const designHtml = designFile.replace(/design\.json$/, 'design.html');
        return path.posix.relative(ARCH_OUTPUT_DIR, designHtml);
    }

    /**
     * The design pages that EXIST, one entry per visible node that has one. A node absent from this
     * list gets no "View Design" item at all — the item is never rendered dead or greyed out.
     */
    designLinks(graph: EnhancedGraph): DesignLink[] {
        const links: DesignLink[] = [];
        for (const project of Object.keys(graph)) {
            const info = graph[project];
            if (this.isHidden(info)) continue;
            const href = this.designHtmlHref(info.designFile);
            if (href === null) continue;
            links.push(new DesignLink(this.names.getNodeId(project), href));
        }
        return links;
    }

    /**
     * Generate Graphviz DOT format from the graph
     */
    generateDot(graph: EnhancedGraph, title: string = 'Monorepo Dependency Architecture'): string {
        this.assertDrawable(graph);
        let dot = 'digraph Architecture {\n';
        dot += '  rankdir=TB;\n';
        dot += '  node [shape=box, style=filled, fontname="Arial"];\n';
        dot += '  edge [fontname="Arial"];\n\n';

        const bands = this.levelBands(graph);

        dot += this.dotNodes(graph);
        dot += '\n';
        dot += this.bandLayout.dot(bands);
        dot += '\n';
        dot += this.dotEdges(graph);

        dot += '\n  labelloc="t";\n';
        dot += `  label="${title}\\n(from architecture/dependencies.json)";\n`;
        dot += '  fontsize=20;\n';
        dot += '}\n';

        return dot;
    }

    /**
     * Refuse to draw a graph that would render something false.
     *
     * Two conditions, both of which produced a confidently-wrong picture with no warning at all:
     *  - two projects sharing a node id fuse into one box, which UNIONS their two rank sets and
     *    collapses two whole dependency levels onto one row (see graph-names.ts);
     *  - a cycle makes the level numbers every row is keyed on meaningless (see graph-cycles.ts).
     */
    private assertDrawable(graph: EnhancedGraph): void {
        const owners: NodeIdOwner[] = [];
        const adjacency: ProjectAdjacency = {};
        for (const project of Object.keys(graph)) {
            owners.push(new NodeIdOwner(project, graph[project].level));
            adjacency[project] = graph[project].dependsOn ?? [];
        }
        this.names.assertUniqueNodeIds(owners);
        this.cycles.assertAcyclic(adjacency, 'architecture/dependencies.json');
    }

    /**
     * The visible projects grouped into one band per dependency level, ordered HIGHEST LEVEL FIRST
     * so the emitted bands read top-to-bottom with L0 last (hidden projects are omitted, so no
     * stray rank=same name is emitted for an absent node). LevelBandLayout turns these into the
     * rank sets and the invisible chain that pins them — see graph-level-bands.ts for why the
     * chain is required at all.
     */
    private levelBands(graph: EnhancedGraph): LevelBand[] {
        const byLevel = new Map<number, string[]>();
        for (const project of Object.keys(graph)) {
            if (this.isHidden(graph[project])) continue;
            const level = graph[project].level;
            const nodeIds = byLevel.get(level);
            if (nodeIds === undefined) byLevel.set(level, [this.names.getNodeId(project)]);
            else nodeIds.push(this.names.getNodeId(project));
        }
        const levels = [...byLevel.keys()].sort((a: number, b: number): number => b - a);
        return levels.map((level: number): LevelBand =>
            new LevelBand(level, [...(byLevel.get(level) as string[])].sort()));
    }

    // Node lines: fill colored by framework env set (libType), border shaped by
    // role; the label shows the env set + role (e.g. [browser, node] · server).
    // No node carries a URL: EVERY box is clickable and opens the floating node
    // menu instead, which is where a design page is reached (see designLinks).
    private dotNodes(graph: EnhancedGraph): string {
        let dot = '';
        for (const project of Object.keys(graph)) {
            const info = graph[project];
            if (this.isHidden(info)) continue;
            const nodeId = this.names.getNodeId(project);
            const shortName = this.names.getShortName(project);
            const frameworks = info.framework ?? [];
            const role = info.role ?? 'lib';
            const color = this.frameworkColor(frameworks);
            const border = this.roleBorderAttrs(role);
            const envSet = `[${frameworks.join(', ')}]`;
            const labelMeta = `L${info.level} · ${envSet} · ${role}`;
            // Identity is the project key; the LABEL is the pretty short name.
            dot += `  "${nodeId}" [fillcolor="${color}"${border}, label="${shortName}\\n(${labelMeta})"];\n`;
        }
        return dot;
    }

    // Edge lines (dependencies). An edge to an api-lib is styled by WHY it exists
    // (implements/uses/uses-implements, from apiRelations); every other dependency
    // keeps the default plain arrow.
    private dotEdges(graph: EnhancedGraph): string {
        let dot = '';
        for (const project of Object.keys(graph)) {
            const info = graph[project];
            if (this.isHidden(info)) continue;
            const nodeId = this.names.getNodeId(project);
            for (const dep of info.dependsOn || []) {
                // Both endpoints must be visible — an edge to/from a hidden box
                // is dropped so no connection dangles into empty space.
                if (graph[dep] !== undefined && this.isHidden(graph[dep])) continue;
                dot += this.edgeDot(nodeId, this.names.getNodeId(dep), info.apiRelations?.[dep]);
            }
        }
        return dot;
    }

    /**
     * Generate interactive HTML with embedded SVG using viz.js
     */
    generateHTML(
        dot: string,
        links: DesignLink[],
        title: string = 'Monorepo Dependency Architecture',
        lockControl: string = '',
        responsibilitiesHtml: string = ''
    ): string {
        const styles = this.styles();
        const legend = this.legend();
        const script = this.script(dot, links);

        return `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>${title}</title>
    <script src="https://cdn.jsdelivr.net/npm/@viz-js/viz@3.28.0/dist/viz-global.js"></script>
    <style>${styles}</style>
</head>
<body>
    <h1>${title}</h1>
    <p class="hint">💡 <strong>Click any box</strong> for its menu — <strong>View Design</strong> (only where that project has a generated <strong>design.html</strong>, i.e. what the AI sees inside it) and <strong>Lock/Unlock</strong>, which is the same lock as the dropdown below.</p>
    <p class="hint">🔦 <strong>Hover any box</strong> to trace its <em>entire</em> dependency chain — every ancestor above it (all the way up) <em>and</em> every dependency below it (all the way down), with all the boxes and lines between — while the rest of the graph dims so you can follow one box at a glance.</p>
    ${legend}
    ${lockControl}
    <div id="graph"></div>
    ${responsibilitiesHtml}
    <script>${this.nodeMenu.script()}</script>
    <script>${script}</script>
</body>
</html>`;
    }

    /**
     * The lock control (a single-select dropdown, rendered below the legend).
     * Picking a module LOCKS the graph into that box's hover view — its full
     * ancestor + descendant chain stays lit while everything else stays dimmed —
     * and narrows the responsibilities list below the graph to just that chain.
     * The first option, "All", is the default and clears the lock. Hover still
     * works on top of a lock; leaving a box returns to the locked view.
     *
     * Options are ordered by level DESCENDING to match the responsibilities cards.
     */
    lockControl(graph: EnhancedGraph): string {
        const projects = Object.keys(graph);
        projects.sort((a: string, b: string): number => {
            const levelDiff = graph[b].level - graph[a].level;
            if (levelDiff !== 0) return levelDiff;
            return a.localeCompare(b);
        });
        let options = '';
        for (const project of projects) {
            if (this.isHidden(graph[project])) continue;
            // The VALUE is the node id the SVG is keyed on; the TEXT stays the pretty short name.
            const nodeId = this.names.getNodeId(project);
            const shortName = this.names.getShortName(project);
            options += `<option value="${nodeId}">L${graph[project].level} · ${shortName}</option>`;
        }
        return `<div class="wp-lock-control">
        <label for="wp-lock">🔒 Lock a box (dim the rest &amp; filter responsibilities):</label>
        <select id="wp-lock"><option value="">All (no lock)</option>${options}</select>
    </div>`;
    }

    private styles(): string {
        return `
        body { margin: 0; padding: 20px; font-family: Arial, sans-serif; background: #f5f5f5; }
        h1 { text-align: center; color: #333; }
        .hint { text-align: center; color: #555; margin: 0 0 16px; }
        /* Every box is clickable (the floating node menu replaced direct
         * navigation), so the menu's own stylesheet carries the cursor + blue
         * glow, and the dim/undim rules a hover or a lock toggles. Both are
         * shared verbatim with the per-project design pages. */
        ${this.nodeMenu.styles()}
        ${this.nodeMenu.dimStyles('#graph')}
        #graph {
            text-align: center;
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .legend {
            margin: 20px auto;
            max-width: 1100px;
            padding: 15px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .legend h2 { margin-top: 0; }
        .legend-item { margin: 8px 0; }
        .legend-box {
            display: inline-block;
            width: 20px;
            height: 20px;
            border: 1px solid #ccc;
            margin-right: 10px;
            vertical-align: middle;
        }
        ${this.componentStyles()}
    `;
    }

    // Styles for the lock dropdown and the responsibilities card list below the
    // graph. Split out of styles() to keep each method within the line limit.
    private componentStyles(): string {
        return `
        /* The architecture graph is very wide, so lay the legend out as three
         * side-by-side columns (fill / border / edge) instead of one tall
         * column — it keeps the legend short next to the wide graph, and
         * collapses back to a single column on narrow viewports. */
        .legend-columns {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 28px;
            align-items: start;
        }
        .legend-col h3 { margin: 0 0 8px; color: #333; font-size: 15px; }
        @media (max-width: 800px) { .legend-columns { grid-template-columns: 1fr; } }
        .wp-lock-control {
            max-width: 600px;
            margin: 0 auto 16px;
            padding: 12px 15px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            text-align: center;
        }
        .wp-lock-control label { font-weight: bold; color: #333; margin-right: 8px; }
        .wp-lock-control select { font-size: 14px; padding: 4px 8px; }
        #wp-responsibilities { max-width: 900px; margin: 24px auto 0; }
        #wp-responsibilities h2 { color: #333; }
        .wp-resp-card {
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            margin: 10px 0;
            padding: 10px 15px;
        }
        .wp-resp-card > summary { cursor: pointer; color: #333; }
        .wp-resp-level {
            display: inline-block;
            min-width: 26px;
            padding: 1px 6px;
            margin-right: 6px;
            border-radius: 4px;
            background: #eef;
            font-size: 12px;
            font-weight: bold;
            text-align: center;
        }
        .wp-resp-body { margin-top: 8px; color: #444; }
        .wp-resp-body code {
            background: #f2f2f2;
            padding: 1px 4px;
            border-radius: 3px;
            font-family: monospace;
        }
        .wp-hidden { display: none; }`;
    }

    // The legend is laid out in three side-by-side columns (fill / border /
    // edge) so it stays short next to the very wide architecture graph. Each
    // column's rows come from a helper below to keep this method within the line
    // limit; the footnote spans the full width beneath the columns.
    private legend(): string {
        return `<div class="legend">
        <h2>Legend</h2>
        <div class="legend-columns">
            <div class="legend-col">
                <h3>Fill = framework (libType)</h3>
                ${this.fillItems()}
            </div>
            <div class="legend-col">
                <h3>Border = role</h3>
                ${this.borderItems()}
            </div>
            <div class="legend-col">
                <h3>Edge lines — <em>why</em> a project depends on an api-lib</h3>
                ${this.edgeItems()}
            </div>
        </div>
        <div class="legend-item" style="margin-top: 15px;">
            <em>Each node label shows its dependency level (L#), its framework env set (e.g. [browser, node]), and its role. Every row is one dependency level and nothing else: the HIGHEST level is the top row, levels descend as you read down, and L0 — the foundation libraries everything else is built on — is always the bottom row. Transitive dependencies are allowed but not shown.</em>
        </div>
    </div>`;
    }

    // Column 1 — fill color keyed on the project's framework (libType) env set.
    private fillItems(): string {
        return `<div class="legend-item">
            <span class="legend-box" style="background: #FCE4EC;"></span>
            <strong>angular:</strong> Angular front-end
        </div>
        <div class="legend-item">
            <span class="legend-box" style="background: #E3F2FD;"></span>
            <strong>react:</strong> React front-end
        </div>
        <div class="legend-item">
            <span class="legend-box" style="background: #EDE7F6;"></span>
            <strong>browser:</strong> browser front-end base env
        </div>
        <div class="legend-item">
            <span class="legend-box" style="background: #E8F5E9;"></span>
            <strong>express:</strong> Express / server side
        </div>
        <div class="legend-item">
            <span class="legend-box" style="background: #FFF9C4;"></span>
            <strong>node:</strong> node server base env
        </div>`;
    }

    // Column 2 — border style keyed on the project's role.
    private borderItems(): string {
        return `<div class="legend-item">
            <span class="legend-box" style="border: 3px solid green;"></span>
            <strong>server:</strong> runnable server app (thick green border)
        </div>
        <div class="legend-item">
            <span class="legend-box" style="border: 3px solid red;"></span>
            <strong>client:</strong> client app, e.g. angular (thick red border)
        </div>
        <div class="legend-item">
            <span class="legend-box" style="border: 2px solid #333;"></span>
            <strong>designed-lib:</strong> library with a generated @DocumentDesign design (bold border)
        </div>
        <div class="legend-item">
            <span class="legend-box" style="border: 1px solid #ccc;"></span>
            <strong>lib:</strong> plain library, no generated design (thin border)
        </div>
        <div class="legend-item">
            <span class="legend-box" style="border: 2px solid #EF6C00;"></span>
            <strong>api-lib:</strong> API-contract library (defines <code>@ApiPath</code>/<code>@Rpc</code>/<code>@PubSub</code> <code>*Api</code> classes)
        </div>`;
    }

    // Column 3 — edge line style keyed on WHY a project depends on an api-lib.
    private edgeItems(): string {
        return `<div class="legend-item">
            <svg width="42" height="12" style="vertical-align: middle; margin-right: 10px;"><line x1="0" y1="6" x2="42" y2="6" stroke="#333" stroke-width="2"/></svg>
            <strong>uses:</strong> calls the API (generates an rpc/pubsub client via <code>createRpcClient</code>/<code>createPubSubClient</code>) — also covers a plain library import, since a plain dependency is just a use.
        </div>
        <div class="legend-item">
            <svg width="42" height="12" style="vertical-align: middle; margin-right: 10px;"><line x1="0" y1="6" x2="42" y2="6" stroke="#333" stroke-width="2" stroke-dasharray="5,3"/></svg>
            <strong>implements:</strong> serves the API — labeled <code>implements: &lt;contracts&gt;</code> so you can see WHICH contracts resolve to this server without opening the JSON. NOTE: this is a build-dependency diagram, so a UML <em>implements</em> arrow can't be used; we use a dashed line to signal a build dep, because this server implements the api and the api is built first, then this server after.
        </div>
        <div class="legend-item">
            <svg width="42" height="12" style="vertical-align: middle; margin-right: 10px;"><line x1="0" y1="6" x2="42" y2="6" stroke="#1976d2" stroke-width="2" stroke-dasharray="5,3"/></svg>
            <strong>uses/implements:</strong> both — implements some of the api-lib's contracts, uses others
        </div>`;
    }

    /**
     * The page script. The browser code lives in graph-visualizer.client.ts — real, linted TypeScript
     * that tsc compiles in place, so what this inlines is its COMPILED .js sitting beside this file.
     *
     * The substitution is a blind split/join, which is why the placeholder token must appear EXACTLY
     * ONCE in the client (never in one of its comments): every literal occurrence would otherwise be
     * replaced by the whole DOT.
     */
    private script(dot: string, links: DesignLink[]): string {
        return this.clientJs()
            .split(CLIENT_DOT_PLACEHOLDER).join(JSON.stringify(dot))
            .split(CLIENT_DESIGN_LINKS_PLACEHOLDER).join(JSON.stringify(links));
    }

    /**
     * Write the committed architecture visualization to
     * architecture/dependencies.html, next to dependencies.json.
     *
     * This is a checked-in artifact, regenerated deterministically by
     * architecture:generate so every box keeps its menu, and the "View Design"
     * item keeps pointing at each project's committed design.html as designs
     * come and go. The DOT is embedded in the HTML (rendered client-side by
     * viz.js). Output is deterministic (sorted graph in → same bytes out) so git
     * only shows a diff when the architecture actually changed.
     */
    writeVisualization(
        graph: EnhancedGraph,
        workspaceRoot: string,
        title: string = 'Monorepo Dependency Architecture'
    ): VisualizationPaths {
        const outputDir = path.join(workspaceRoot, ARCH_OUTPUT_DIR);

        if (!fs.existsSync(outputDir)) {
            fs.mkdirSync(outputDir, { recursive: true });
        }

        const lockControl = this.lockControl(graph);
        const responsibilities = this.responsibilities.generateSection(graph, workspaceRoot);
        const html = this.generateHTML(
            this.generateDot(graph, title), this.designLinks(graph), title, lockControl, responsibilities);
        const htmlPath = path.join(outputDir, 'dependencies.html');
        fs.writeFileSync(htmlPath, html, 'utf-8');

        return new VisualizationPaths(htmlPath);
    }

    /**
     * Open the HTML visualization in the default browser
     */
    openVisualization(htmlPath: string): boolean {
        // eslint-disable-next-line @webpieces/no-unmanaged-exceptions
        try {
            const platform = process.platform;
            let openCommand: string;

            if (platform === 'darwin') {
                openCommand = `open "${htmlPath}"`;
            } else if (platform === 'win32') {
                openCommand = `start "" "${htmlPath}"`;
            } else {
                openCommand = `xdg-open "${htmlPath}"`;
            }

            execSync(openCommand, { stdio: 'ignore' });
            return true;
        } catch (err: unknown) {
            const error = toError(err);
            console.warn(`⚠️  Could not open browser: ${error.message}`);
            return false;
        }
    }
}
